import { useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

import Modal from "../../layouts/Modal";
import LoadingWheel from "../shared/LoadingWheel";
import useNotification from "../notifications/useNotifications";
import classNames from "../../utils/classNames";
import { ICheckInItem, ICheckInStep } from "../../models/CheckIn";
import { IVehicle } from "../../models/Vehicle";
import {
  clearLocalCheckInItems,
  isPendingStepId,
  loadLocalCheckInItems,
  mergeCheckInSteps,
  saveLocalCheckInItem,
} from "./checkInSteps";
import DamageDiagramEditor from "./DamageDiagramEditor";

interface VehicleCheckInModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  vehicle: IVehicle;
  steps: ICheckInStep[];
  items: ICheckInItem[];
  isLoading: boolean;
  saveCheckInItem: {
    mutate: (body: any, opts?: any) => void;
  };
}

// Heuristics for deciding how each backend-defined step should be rendered.
// Keeping it name-driven means we don't need to change the model — whoever
// seeds the steps table just needs matching names.
const normaliseName = (step: ICheckInStep) => step.name.trim().toLowerCase();

// Free-text steps that benefit from a multi-line textarea rather than a single
// input (e.g. hazards, general notes). Keep this list loose so ops teams can
// add similar steps without a UI change.
const isTextareaStep = (step: ICheckInStep) => {
  const n = normaliseName(step);
  return n === "hazards" || n.endsWith(" notes") || n === "notes";
};

// Binary confirmation steps that operators progress from a dedicated UI
// elsewhere (uploading photos). We render them as a simple "confirmed" button
// here and rely on the dedicated flow for the actual content, matching the
// product scope of the check-in report.
const isExternalActionStep = (step: ICheckInStep) => {
  const n = normaliseName(step);
  return n === "vehicle images";
};

// Dedicated interactive renderer — operators drop severity pins on a
// top-down car diagram rather than filling in a value manually.
const isDamageDiagramStep = (step: ICheckInStep) =>
  normaliseName(step) === "damage diagram";

const parseOptions = (options: string | null): string[] => {
  if (!options) return [];
  return options
    .split(/[,|]/)
    .map((o) => o.trim())
    .filter(Boolean);
};

// Semantic colouring for common option labels so the UI feels more informative
// than a generic chip.
const optionClasses = (option: string): string => {
  const lowered = option.toLowerCase();
  if (["pass", "yes", "excellent", "good"].includes(lowered)) {
    return "data-[active=true]:bg-emerald-600 data-[active=true]:text-white data-[active=true]:ring-emerald-600";
  }
  if (["faulty", "no", "poor", "heavy damage", "parts missing"].includes(lowered)) {
    return "data-[active=true]:bg-rose-600 data-[active=true]:text-white data-[active=true]:ring-rose-600";
  }
  if (["n/a", "na"].includes(lowered)) {
    return "data-[active=true]:bg-slate-600 data-[active=true]:text-white data-[active=true]:ring-slate-600";
  }
  return "data-[active=true]:bg-primary-600 data-[active=true]:text-white data-[active=true]:ring-primary-600";
};

export default function VehicleCheckInModal({
  open,
  setOpen,
  vehicle,
  steps,
  items,
  isLoading,
  saveCheckInItem,
}: VehicleCheckInModalProps) {
  const { addNotification } = useNotification();
  const [savingStepId, setSavingStepId] = useState<string | null>(null);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  // Bump this when a local save lands so the `localItems` memo below re-reads
  // from `localStorage` — `localStorage` isn't reactive on its own.
  const [localRevision, setLocalRevision] = useState(0);

  // Synthesised items loaded from localStorage for canonical questions the
  // backend hasn't seeded yet. They're merged in below so the operator sees
  // their prior answers and progress even though those answers never hit the
  // API.
  const localItems = useMemo(
    () => loadLocalCheckInItems(vehicle.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicle.id, localRevision]
  );

  const combinedItems = useMemo<ICheckInItem[]>(() => {
    const byStepId = new Map<string, ICheckInItem>();
    // Server items win when both exist — they're the source of truth once the
    // backend is seeded. Local items fill the gaps for pending-only steps.
    for (const item of localItems) byStepId.set(item.checkInStepId, item);
    for (const item of items) byStepId.set(item.checkInStepId, item);
    return Array.from(byStepId.values());
  }, [items, localItems]);

  useEffect(() => {
    // Hydrate the form from the latest merged items — but only replace state
    // when the values genuinely differ from the current draft. Otherwise, a
    // new `items` array reference on every parent render (when the prop
    // falls back to `[]` from `checkInItems.data?.data ?? []`) would fire
    // this effect every pass, resetting state and, in some subscription
    // paths, triggering a "Maximum update depth exceeded" loop.
    setDraftValues((prev) => {
      const next: Record<string, string> = {};
      for (const item of combinedItems)
        next[item.checkInStepId] = item.value ?? "";

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length) return next;
      for (const k of nextKeys) {
        if (prev[k] !== next[k]) return next;
      }
      return prev;
    });
  }, [combinedItems]);

  const itemsByStepId = useMemo(() => {
    const map: Record<string, ICheckInItem | undefined> = {};
    for (const item of combinedItems) map[item.checkInStepId] = item;
    return map;
  }, [combinedItems]);

  // Merge the backend step list with our canonical front-end set so the modal
  // can render the latest question list even when the backend hasn't been
  // re-seeded yet. Leftover backend-only steps are dropped — the new report
  // only surfaces the 10 canonical questions.
  const mergedSteps = useMemo(() => mergeCheckInSteps(steps).steps, [steps]);

  const completedCount = useMemo(
    () => mergedSteps.filter((s) => !!itemsByStepId[s.id]?.completed).length,
    [mergedSteps, itemsByStepId]
  );

  const progressPct = mergedSteps.length
    ? Math.round((completedCount / mergedSteps.length) * 100)
    : 0;

  const vehicleLabel = [vehicle.combinedMake, vehicle.combinedModel]
    .filter(Boolean)
    .join(" ")
    .trim();

  const saveItem = (
    stepId: string,
    value: string,
    isCompleted: boolean
  ) => {
    // Pending (client-only) steps have no backend row yet, so route those
    // saves to `localStorage`. Behaviour from the operator's point of view is
    // identical — the answer persists across refreshes and the progress bar
    // reflects it — but the record never hits `/checkin/items` until the
    // backend seeds a real step with the matching name.
    if (isPendingStepId(stepId)) {
      saveLocalCheckInItem(vehicle.id, stepId, value, isCompleted);
      setLocalRevision((n) => n + 1);
      return;
    }

    setSavingStepId(stepId);
    saveCheckInItem.mutate(
      {
        vehicleId: vehicle.id,
        checkInStepId: stepId,
        value,
        isCompleted,
      },
      {
        onError: () => {
          addNotification({
            variant: "error",
            primaryText: "Unable to update check-in item",
          });
        },
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const handleToggleComplete = (step: ICheckInStep) => {
    const current = itemsByStepId[step.id];
    const nextCompleted = !current?.completed;
    saveItem(step.id, draftValues[step.id] ?? "", nextCompleted);
  };

  const handleSelectOption = (step: ICheckInStep, option: string) => {
    setDraftValues((prev) => ({ ...prev, [step.id]: option }));
    // Selecting an option implicitly marks the step complete.
    saveItem(step.id, option, true);
  };

  const handleTextCommit = (step: ICheckInStep, value: string) => {
    const trimmed = value.trim();
    setDraftValues((prev) => ({ ...prev, [step.id]: trimmed }));
    saveItem(step.id, trimmed, trimmed.length > 0);
  };

  // Damage diagram edits arrive rapidly (every click, every keystroke in a
  // pin's note field). We still want the answer autosaved like every other
  // step, but firing a save per keystroke would flood the API once the
  // backend is seeded — so we debounce with a short trailing delay. Pin
  // adds/removes still feel instant at 250ms.
  const diagramSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );
  const handleDiagramChange = (
    step: ICheckInStep,
    nextValue: string,
    hasMarks: boolean
  ) => {
    setDraftValues((prev) => ({ ...prev, [step.id]: nextValue }));
    const existing = diagramSaveTimers.current[step.id];
    if (existing) clearTimeout(existing);
    diagramSaveTimers.current[step.id] = setTimeout(() => {
      saveItem(step.id, nextValue, hasMarks);
    }, 250);
  };

  useEffect(() => {
    return () => {
      // Flush any pending diagram debounce timers on unmount so we don't leak
      // them across mounts.
      const timers = diagramSaveTimers.current;
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  // Reset / restart flow. The button is a two-step confirm so operators can't
  // nuke a completed report by accident. Clearing wipes local answers
  // immediately and blanks every server-backed answer via the existing save
  // endpoint — a dedicated reset endpoint would be cleaner but this keeps
  // the backend contract unchanged.
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const beginReset = () => setConfirmingReset(true);
  const cancelReset = () => setConfirmingReset(false);

  const performReset = () => {
    setIsResetting(true);
    // Cancel any in-flight diagram debounces so they don't re-save after we
    // wipe their step.
    Object.values(diagramSaveTimers.current).forEach((t) => clearTimeout(t));
    diagramSaveTimers.current = {};

    // Pending (client-only) answers: drop them in one go.
    clearLocalCheckInItems(vehicle.id);

    // Server-backed answers: blank each row that had anything recorded.
    const serverAnswered = items.filter(
      (i) =>
        !isPendingStepId(i.checkInStepId) &&
        ((i.value ?? "").length > 0 || !!i.completed)
    );

    const finish = (hadError: boolean) => {
      setDraftValues({});
      setLocalRevision((n) => n + 1);
      setIsResetting(false);
      setConfirmingReset(false);
      addNotification({
        variant: hadError ? "error" : "success",
        primaryText: hadError
          ? "Some answers could not be cleared"
          : "Check-in reset",
      });
    };

    if (serverAnswered.length === 0) {
      finish(false);
      return;
    }

    let remaining = serverAnswered.length;
    let hadError = false;
    for (const item of serverAnswered) {
      saveCheckInItem.mutate(
        {
          vehicleId: vehicle.id,
          checkInStepId: item.checkInStepId,
          value: "",
          isCompleted: false,
        },
        {
          onError: () => {
            hadError = true;
          },
          onSettled: () => {
            remaining -= 1;
            if (remaining === 0) finish(hadError);
          },
        }
      );
    }
  };

  const hasAnyAnswer = useMemo(
    () =>
      combinedItems.some(
        (i) => (i.value ?? "").length > 0 || !!i.completed
      ),
    [combinedItems]
  );

  const stepsSorted = useMemo(() => {
    // Ordering flows from visual capture → identity verification → keys →
    // mechanical checks → condition assessments → hazards/damage so the
    // operator walks the vehicle in a natural, tactile order.
    const priority = [
      "vehicle images",
      "vrm & vin verified",
      "keys",
      "starts and drives",
      "engine condition",
      "gearbox condition",
      "exterior condition",
      "interior condition",
      "hazards",
      "damage diagram",
    ];

    const weightFor = (name: string) => {
      const idx = priority.indexOf(name.trim().toLowerCase());
      return idx === -1 ? priority.length : idx;
    };

    return [...mergedSteps].sort(
      (a, b) => weightFor(a.name) - weightFor(b.name)
    );
  }, [mergedSteps]);

  const renderStep = (step: ICheckInStep) => {
    const item = itemsByStepId[step.id];
    const isCompleted = !!item?.completed;
    const isSaving = savingStepId === step.id;
    const disabled = isSaving;
    const currentValue = draftValues[step.id] ?? "";
    const options = parseOptions(step.options);
    const isDiagram = isDamageDiagramStep(step);

    // Card shell is the same for every step — only the control changes.
    // Damage diagram is the one exception: it needs the full modal width to
    // show the car outline plus the pin list side-by-side, so it spans both
    // columns of the parent grid.
    return (
      <div
        key={step.id}
        className={classNames(
          "rounded-xl border bg-white p-4 transition-all",
          isCompleted
            ? "border-emerald-200 bg-emerald-50/40"
            : "border-slate-200 hover:border-slate-300",
          isDiagram && "sm:col-span-2"
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800">{step.name}</p>
            {step.needsValue &&
              options.length === 0 &&
              !isTextareaStep(step) &&
              !isDiagram && (
                <p className="mt-0.5 text-xs text-slate-500">
                  Enter a value to mark as complete.
                </p>
              )}
            {step.needsValue && options.length > 0 && (
              <p className="mt-0.5 text-xs text-slate-500">
                Pick the option that best describes the current state.
              </p>
            )}
            {step.needsValue && isTextareaStep(step) && (
              <p className="mt-0.5 text-xs text-slate-500">
                Record any hazards or notes the dismantler should be aware of.
              </p>
            )}
            {isDiagram && (
              <p className="mt-0.5 text-xs text-slate-500">
                Click the diagram to mark damage. Use severity to colour-code
                each area; re-select a mark to change it or add a note.
              </p>
            )}
            {!step.needsValue && isExternalActionStep(step) && (
              <p className="mt-0.5 text-xs text-slate-500">
                Capture vehicle photos before ticking this off.
              </p>
            )}
          </div>

          <span
            className={classNames(
              "inline-flex flex-none items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
              isCompleted
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            )}
          >
            {isCompleted ? (
              <>
                <CheckIcon className="h-3 w-3" aria-hidden="true" /> Done
              </>
            ) : (
              "Pending"
            )}
          </span>
        </div>

        <div className="mt-3">
          {isDiagram ? (
            <DamageDiagramEditor
              value={currentValue}
              disabled={disabled}
              onChange={(nextValue, hasMarks) =>
                handleDiagramChange(step, nextValue, hasMarks)
              }
            />
          ) : !step.needsValue ? (
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleToggleComplete(step)}
              className={classNames(
                "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ring-1 transition-all",
                "focus:outline-none focus:ring-2 focus:ring-primary-400",
                isCompleted
                  ? "bg-emerald-600 text-white ring-emerald-600 hover:bg-emerald-700"
                  : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50",
                isSaving && "cursor-wait opacity-70"
              )}
            >
              <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
              {isCompleted
                ? "Confirmed"
                : isExternalActionStep(step)
                  ? "Mark images captured"
                  : "Mark as confirmed"}
            </button>
          ) : options.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {options.map((opt) => {
                const active = currentValue === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    data-active={active}
                    disabled={disabled}
                    onClick={() => handleSelectOption(step, opt)}
                    className={classNames(
                      "rounded-lg px-3 py-1.5 text-sm font-medium ring-1 ring-slate-300 bg-white text-slate-700 transition-all",
                      "hover:ring-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-400",
                      optionClasses(opt),
                      isSaving && "cursor-wait opacity-70"
                    )}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          ) : isTextareaStep(step) ? (
            <div className="flex items-start gap-2">
              <textarea
                rows={3}
                value={currentValue}
                onChange={(e) =>
                  setDraftValues((prev) => ({
                    ...prev,
                    [step.id]: e.target.value,
                  }))
                }
                onBlur={(e) => handleTextCommit(step, e.target.value)}
                disabled={disabled}
                placeholder="Describe any hazards (e.g. fluid leaks, airbag deployed)…"
                className="block w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {currentValue && (
                <button
                  type="button"
                  onClick={() => handleToggleComplete(step)}
                  disabled={disabled}
                  className={classNames(
                    "flex-none rounded-md px-2 py-1 text-xs font-medium ring-1 transition-all",
                    isCompleted
                      ? "bg-emerald-600 text-white ring-emerald-600"
                      : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
                  )}
                >
                  {isCompleted ? "Done" : "Mark done"}
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={currentValue}
                onChange={(e) =>
                  setDraftValues((prev) => ({
                    ...prev,
                    [step.id]: e.target.value,
                  }))
                }
                onBlur={(e) => handleTextCommit(step, e.target.value)}
                disabled={disabled}
                placeholder="Enter value"
                className="block w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {currentValue && (
                <button
                  type="button"
                  onClick={() => handleToggleComplete(step)}
                  disabled={disabled}
                  className={classNames(
                    "rounded-md px-2 py-1 text-xs font-medium ring-1 transition-all",
                    isCompleted
                      ? "bg-emerald-600 text-white ring-emerald-600"
                      : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50"
                  )}
                >
                  {isCompleted ? "Done" : "Mark done"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal open={open} setOpen={setOpen as any} width="max-w-4xl">
      <div className="text-left">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-primary-600">
              <ClipboardDocumentCheckIcon
                className="h-5 w-5"
                aria-hidden="true"
              />
              <span className="text-xs font-semibold uppercase tracking-wide">
                Vehicle Check-In
              </span>
            </div>
            <h3 className="mt-1 text-xl font-semibold text-slate-900">
              {vehicleLabel || "Vehicle"}
              {vehicle.vrm && (
                <span className="ml-2 inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-sm font-semibold text-slate-700">
                  {vehicle.vrm}
                </span>
              )}
            </h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Verify and record the condition of the donor vehicle. Selections
              save automatically.
            </p>
          </div>

          <button
            type="button"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs font-medium text-slate-500">
              <span>Progress</span>
              <span>
                {completedCount} of {steps.length} complete
              </span>
            </div>
            <div className="mt-1 h-2 rounded-full bg-slate-100">
              <div
                className={classNames(
                  "h-2 rounded-full transition-all duration-300",
                  progressPct === 100 ? "bg-emerald-500" : "bg-primary-500"
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <span
            className={classNames(
              "rounded-full px-2.5 py-1 text-xs font-semibold",
              progressPct === 100
                ? "bg-emerald-100 text-emerald-700"
                : "bg-primary-100 text-primary-700"
            )}
          >
            {progressPct}%
          </span>
        </div>

        {/* Body */}
        <div className="mt-5 max-h-[70vh] overflow-y-auto pr-1">
          {isLoading ? (
            <div className="py-10">
              <LoadingWheel />
            </div>
          ) : stepsSorted.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
              No check-in steps have been configured yet. Ask an admin to add
              them on the server.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {stepsSorted.map(renderStep)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            {!confirmingReset ? (
              <button
                type="button"
                onClick={beginReset}
                disabled={!hasAnyAnswer || isResetting}
                className={classNames(
                  "inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-600 ring-1 ring-inset ring-slate-300 transition-all",
                  "hover:bg-slate-50 hover:text-slate-700",
                  "focus:outline-none focus:ring-2 focus:ring-primary-500",
                  (!hasAnyAnswer || isResetting) &&
                    "cursor-not-allowed opacity-60 hover:bg-white hover:text-slate-600"
                )}
                title={
                  hasAnyAnswer
                    ? "Clear every answer and start over"
                    : "No answers to clear"
                }
              >
                <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                Reset check-in
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={performReset}
                  disabled={isResetting}
                  className={classNames(
                    "inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-all",
                    "hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500",
                    isResetting && "cursor-wait opacity-70"
                  )}
                >
                  <ArrowPathIcon
                    className={classNames(
                      "h-4 w-4",
                      isResetting && "animate-spin"
                    )}
                    aria-hidden="true"
                  />
                  {isResetting ? "Resetting…" : "Confirm reset"}
                </button>
                <button
                  type="button"
                  onClick={cancelReset}
                  disabled={isResetting}
                  className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <span className="text-xs text-slate-500">
                  This clears every answer for this vehicle.
                </span>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}
